import { CommentForm, CommentFormHandle, ReplyContext } from './comment-form'
import { CommentThread } from './comment-thread'
import { useComments } from '../../hook/use-community'
import { CommunityEntityType } from '../../types/community'
import { Skeleton } from '../ui/skeleton'
import React, { useCallback, useMemo, useRef, useState } from 'react'

interface CommentsSectionProps {
  entityType: CommunityEntityType
  entityId: string
  compact?: boolean
  small?: boolean
}

export const CommentsSection = ({ entityId, entityType, compact, small }: CommentsSectionProps): React.ReactElement => {
  const commentsQuery = useComments({
    entityType,
    entityId,
  })

  const [replyTo, setReplyTo] = useState<ReplyContext | null>(null)
  const formRef = useRef<CommentFormHandle>(null)

  const comments = useMemo(() => commentsQuery.data?.pages.flatMap((page) => page.data) ?? [], [commentsQuery.data])
  const totalCount = commentsQuery.data?.pages[0]?.pagination.totalSize ?? 0
  const hasMore = commentsQuery.hasNextPage ?? false

  const handleReply = useCallback((commentId: string, username: string) => {
    setReplyTo({ commentId, username })
    setTimeout(() => formRef.current?.focus(), 0)
  }, [])

  const handleClearReply = useCallback(() => {
    setReplyTo(null)
  }, [])

  return (
    <div className={compact ? '' : 'mt-6 mb-8'}>
      {!compact && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm font-semibold text-white/60">Comments</span>
          <span className="text-sm text-white/40">·</span>
          <span className="text-sm text-white/40">{totalCount ?? 0}</span>
        </div>
      )}

      {commentsQuery.isLoading && (
        <div className="mt-3 space-y-3">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}

      {!commentsQuery.isLoading && comments.length === 0 && !compact && (
        <div className="mt-4 py-6 text-center">
          <div className="text-lg font-bold text-white">
            BE THE FIRST HUSTLER TO DROP <span className="text-primary">A COMMENT</span> 💬
          </div>
          <div className="mt-1 text-sm font-semibold text-white">TELL US HOW IT FELT!</div>
        </div>
      )}

      <div className={compact ? 'mt-2 divide-y divide-white/[0.06]' : 'mt-4 divide-y divide-white/[0.06]'}>
        {comments.map((comment) => (
          <CommentThread key={comment.id} comment={comment} onReply={handleReply} small={small} />
        ))}
      </div>

      {hasMore && (
        <div className="pt-3">
          <button
            type="button"
            onClick={() => commentsQuery.fetchNextPage()}
            disabled={commentsQuery.isFetchingNextPage}
            className="text-sm font-semibold text-primary hover:text-primary/80"
          >
            {commentsQuery.isFetchingNextPage ? 'Loading more...' : 'Load more comments'}
          </button>
        </div>
      )}

      {commentsQuery.isError && (
        <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          Failed to load comments.{' '}
          <button type="button" className="underline" onClick={() => commentsQuery.refetch()}>
            Retry
          </button>
        </div>
      )}

      <div className="mt-4">
        <CommentForm
          ref={formRef}
          entityId={entityId}
          entityType={entityType}
          replyTo={replyTo}
          onClearReply={handleClearReply}
        />
      </div>
    </div>
  )
}
